import React from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import { srcLight as GetDesignUpdatesIllustration } from '@atlassian/jira-illustrations/src/ui/adg4/jira/heroes/components/get-design-updates/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useIssueId, useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import {
	ContextualAnalyticsData,
	fireUIAnalytics,
	INLINE_DIALOG,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { useProjectId, useProjectKey } from '@atlassian/jira-project-context-service/src/main.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { WebhooksConfigurationLocation } from '../common/types.tsx';
import { FIGMA_PLUGIN_URL, FIGMA_PLUGIN_URL_STG, CONTACT_ADMIN_FORM_URL } from './constants.tsx';
import DesignUpdatesContentHeader from './header/index.tsx';
import messages from './messages.tsx';

const getFigmaPluginUrlForEnv = (env: string) =>
	env === 'prod' ? FIGMA_PLUGIN_URL : FIGMA_PLUGIN_URL_STG;

type GetDesignUpdateContentProps = {
	location: WebhooksConfigurationLocation;
	onClose?: () => void;
};
const GetDesignUpdatesContent = ({ location, onClose }: GetDesignUpdateContentProps) => {
	const { formatMessage } = useIntl();
	const { isAdmin } = useTenantContext();
	const issueId = Number(useIssueId());
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const projectId = useProjectId(projectKey);

	const fireUIAnalyticsEvent = (analyticsEvent: UIAnalyticsEvent, eventName: string) => {
		fireUIAnalytics(analyticsEvent, eventName, {
			webhooksConfigurationLocation: location,
		});
	};

	const environment = useEnvironment();
	const figmaPluginUrl = getFigmaPluginUrlForEnv(environment);

	return (
		<ContextualAnalyticsData
			sourceName="getDesignUpdates"
			sourceType={INLINE_DIALOG}
			attributes={{ issueId, projectId }}
		>
			<GetDesignUpdatesContentContainer
				noPadding={location === WebhooksConfigurationLocation.MODAL}
			>
				<DesignUpdatesContentHeader location={location} onClose={onClose} isAdmin={isAdmin} />
				<GetDesignUpdatesContentDetailsContainer>
					{formatMessage(isAdmin ? messages.descriptionForAdmin : messages.description)}
					<img src={GetDesignUpdatesIllustration} alt="connectAccountIcon" height={85} />
				</GetDesignUpdatesContentDetailsContainer>
				{isAdmin ? (
					// hardcode link for now -> will be replaced by an AGG-fed value
					<Button
						href={figmaPluginUrl}
						onClick={(_, analyticsEvent) =>
							fireUIAnalyticsEvent(analyticsEvent, 'configureFigmaWebhooks')
						}
						interactionName="auto-generated.jira-issue-get-design-updates-content.ui.button"
					>
						{formatMessage(messages.buttonForAdmin)}
					</Button>
				) : (
					<Button
						href={CONTACT_ADMIN_FORM_URL}
						target="_blank"
						onClick={(_, analyticsEvent) => fireUIAnalyticsEvent(analyticsEvent, 'notifyAdmin')}
						interactionName="auto-generated.jira-issue-get-design-updates-content.ui.button1"
					>
						{formatMessage(messages.button)}
					</Button>
				)}
			</GetDesignUpdatesContentContainer>
		</ContextualAnalyticsData>
	);
};

export default GetDesignUpdatesContent;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GetDesignUpdatesContentContainer = styled.div<{ noPadding?: boolean }>(
	{
		maxWidth: '350px',
		flexDirection: 'column',
		gap: `${token('space.050')} `,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => (props.noPadding ? '' : `padding: ${token('space.200')} ${token('space.200')} `),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GetDesignUpdatesContentDetailsContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	gap: `${token('space.050')} `,
	paddingTop: `${token('space.200')}`,
	paddingBottom: `${token('space.200')}`,
});
